/* eslint-disable react-refresh/only-export-components */
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useRef,
  useState,
  ReactNode,
} from 'react';
import {
  PricingData,
  Rule,
  Alert,
  CreateRuleInput,
  UpdateRuleInput,
  CreateAlertInput,
  UpdatePricingInput,
  RuleHistoryEntry,
  RuleHistoryAction,
  RuleVersionComparison,
  RuleFieldDiff,
  DeletedRuleInfo,
  RuleType,
  MinLosConfig,
} from './types';
import { supabase } from '../../services/supabaseClient';
import { PriceStorageService } from '../../services/priceStorageService';
import { getWebHotelierPricingBridge } from '../webhotelier/services/webHotelierPricingBridge';

/**
 * Safely parse JSON - handles both string and already-parsed objects (from Supabase jsonb columns)
 */
const safeParseJson = <T = unknown[],>(value: unknown, fallback: T): T => {
  if (!value) return fallback;
  if (typeof value === 'object') return value as T; // Already parsed by Supabase
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch {
      return fallback;
    }
  }
  return fallback;
};

/**
 * Pricing Context State
 */
interface PricingState {
  pricingData: PricingData[];
  rules: Rule[];
  alerts: Alert[];
  isLoading: boolean;
  error: string | null;
  priceStorageService: PriceStorageService;
}

/**
 * Pricing Context Actions
 */
type PricingAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'UPDATE_PRICING'; payload: PricingData }
  | { type: 'BULK_UPDATE_PRICING'; payload: PricingData[] }
  | { type: 'SYNC_PRICING_DATA'; payload: PricingData[] }
  | { type: 'ADD_RULE'; payload: Rule }
  | { type: 'UPDATE_RULE'; payload: { id: string; updates: Partial<Rule> } }
  | { type: 'DELETE_RULE'; payload: string }
  | { type: 'SET_RULES'; payload: Rule[] }
  | { type: 'ADD_ALERT'; payload: Alert }
  | { type: 'MARK_ALERT_READ'; payload: string }
  | { type: 'DELETE_ALERT'; payload: string }
  | { type: 'SET_ALERTS'; payload: Alert[] };

/**
 * Pricing Context Value
 */
interface PricingContextValue {
  // State
  pricingData: PricingData[];
  rules: Rule[];
  alerts: Alert[];
  isLoading: boolean;
  error: string | null;

  // Pricing Data Actions
  updatePricing: (data: UpdatePricingInput) => Promise<void>;
  bulkUpdatePricing: (data: PricingData[]) => void;
  syncPricingData: (propertyId: string) => Promise<void>;
  getPricingData: (roomId: string, date: string) => PricingData | undefined;

  // Rule Actions
  loadRules: (propertyId: string) => Promise<void>;
  createRule: (input: CreateRuleInput, changeReason?: string) => Promise<Rule>;
  updateRule: (
    id: string,
    updates: UpdateRuleInput,
    changeReason?: string
  ) => Promise<void>;
  deleteRule: (id: string, changeReason?: string) => Promise<void>;
  toggleRuleActive: (id: string, active: boolean) => Promise<void>;

  // Rule History Actions
  getRuleHistory: (ruleId: string) => Promise<RuleHistoryEntry[]>;
  revertToVersion: (ruleId: string, version: number) => Promise<Rule>;
  compareVersions: (
    ruleId: string,
    v1: number,
    v2: number
  ) => Promise<RuleVersionComparison>;
  getDeletedRules: (propertyId: string) => Promise<DeletedRuleInfo[]>;
  restoreDeletedRule: (ruleId: string, version?: number) => Promise<Rule>;

  // Alert Actions
  loadAlerts: (propertyId: string) => Promise<void>;
  createAlert: (input: CreateAlertInput) => void;
  markAlertRead: (id: string) => void;
  deleteAlert: (id: string) => void;
  getUnreadAlerts: () => Alert[];

  // Rule Change Callbacks (for workflow auto-trigger)
  onRuleChange: (callback: RuleChangeCallback) => () => void;
  autoPushEnabled: boolean;
  setAutoPushEnabled: (enabled: boolean) => void;
}

/**
 * Rule Change Callback Type
 */
export type RuleChangeAction = 'created' | 'updated' | 'deleted';
export type RuleChangeCallback = (
  action: RuleChangeAction,
  ruleId: string,
  propertyId: string,
  rule?: Rule
) => void;

/**
 * Pricing Reducer
 */
const pricingReducer = (
  state: PricingState,
  action: PricingAction
): PricingState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };

    case 'UPDATE_PRICING': {
      const existingIndex = state.pricingData.findIndex(
        (data) =>
          data.roomId === action.payload.roomId &&
          data.date === action.payload.date
      );

      if (existingIndex >= 0) {
        return {
          ...state,
          pricingData: state.pricingData.map((data, index) =>
            index === existingIndex
              ? { ...action.payload, lastUpdated: new Date() }
              : data
          ),
          isLoading: false,
        };
      } else {
        return {
          ...state,
          pricingData: [
            ...state.pricingData,
            { ...action.payload, lastUpdated: new Date() },
          ],
          isLoading: false,
        };
      }
    }

    case 'BULK_UPDATE_PRICING': {
      const updatedPricingData = [...state.pricingData];
      action.payload.forEach((newData) => {
        const existingIndex = updatedPricingData.findIndex(
          (data) => data.roomId === newData.roomId && data.date === newData.date
        );
        if (existingIndex >= 0) {
          updatedPricingData[existingIndex] = {
            ...newData,
            lastUpdated: new Date(),
          };
        } else {
          updatedPricingData.push({ ...newData, lastUpdated: new Date() });
        }
      });
      return { ...state, pricingData: updatedPricingData, isLoading: false };
    }

    case 'SYNC_PRICING_DATA':
      return { ...state, pricingData: action.payload, isLoading: false };

    case 'ADD_RULE':
      return {
        ...state,
        rules: [...state.rules, action.payload],
        isLoading: false,
      };

    case 'UPDATE_RULE':
      return {
        ...state,
        rules: state.rules.map((rule) =>
          rule.id === action.payload.id
            ? { ...rule, ...action.payload.updates, lastModified: new Date() }
            : rule
        ),
        isLoading: false,
      };

    case 'DELETE_RULE':
      return {
        ...state,
        rules: state.rules.filter((rule) => rule.id !== action.payload),
        isLoading: false,
      };

    case 'SET_RULES':
      return { ...state, rules: action.payload, isLoading: false };

    case 'ADD_ALERT':
      return {
        ...state,
        alerts: [action.payload, ...state.alerts],
        isLoading: false,
      };

    case 'MARK_ALERT_READ':
      return {
        ...state,
        alerts: state.alerts.map((alert) =>
          alert.id === action.payload ? { ...alert, read: true } : alert
        ),
        isLoading: false,
      };

    case 'DELETE_ALERT':
      return {
        ...state,
        alerts: state.alerts.filter((alert) => alert.id !== action.payload),
        isLoading: false,
      };

    case 'SET_ALERTS':
      return { ...state, alerts: action.payload, isLoading: false };

    default:
      return state;
  }
};

/**
 * Initial State
 */
const initialState: PricingState = {
  pricingData: [],
  rules: [],
  alerts: [],
  isLoading: false,
  error: null,
  priceStorageService: (() => {
    try {
      return new PriceStorageService();
    } catch (error) {
      console.error('Failed to initialize PriceStorageService:', error);
      return {
        savePrice: async () => ({
          success: false,
          errors: [{ field: 'system', error: 'Service unavailable' }],
        }),
        getPrice: async () => null,
        getPrices: async () => [],
        deletePrice: async () => ({
          success: false,
          errors: [{ field: 'system', error: 'Service unavailable' }],
        }),
      } as unknown as PriceStorageService;
    }
  })(),
};

/**
 * Pricing Context
 */
const PricingContext = createContext<PricingContextValue | undefined>(
  undefined
);

/**
 * Pricing Provider Component
 */
const AUTO_PUSH_STORAGE_KEY = 'pricing_context_auto_push';

export const PricingProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(pricingReducer, initialState);

  // Rule change callbacks for workflow auto-trigger
  const ruleChangeCallbacksRef = useRef<Set<RuleChangeCallback>>(new Set());
  const [autoPushEnabled, setAutoPushEnabledState] = useState(() => {
    const stored = localStorage.getItem(AUTO_PUSH_STORAGE_KEY);
    return stored !== null ? stored === 'true' : true;
  });

  const setAutoPushEnabled = useCallback((enabled: boolean) => {
    setAutoPushEnabledState(enabled);
    localStorage.setItem(AUTO_PUSH_STORAGE_KEY, String(enabled));
  }, []);

  const onRuleChange = useCallback(
    (callback: RuleChangeCallback): (() => void) => {
      ruleChangeCallbacksRef.current.add(callback);
      return () => {
        ruleChangeCallbacksRef.current.delete(callback);
      };
    },
    []
  );

  const emitRuleChange = useCallback(
    async (
      action: RuleChangeAction,
      ruleId: string,
      propertyId: string,
      rule?: Rule
    ) => {
      // Notify all registered callbacks
      ruleChangeCallbacksRef.current.forEach((callback) => {
        try {
          callback(action, ruleId, propertyId, rule);
        } catch (error) {
          console.error('[PricingContext] Rule change callback error:', error);
        }
      });

      // Auto-trigger pricing recalculation when rules change
      if (autoPushEnabled && propertyId) {
        console.log(
          `[PricingContext] Rule ${action} detected, triggering auto-recalculation for property ${propertyId}`
        );
        try {
          const pricingBridge = getWebHotelierPricingBridge();

          // Calculate date range: today to 365 days from now (full year ahead)
          const startDate = new Date();
          startDate.setHours(0, 0, 0, 0);
          const endDate = new Date();
          endDate.setDate(endDate.getDate() + 365);
          endDate.setHours(23, 59, 59, 999);

          const result = await pricingBridge.executePricingStrategy(
            propertyId,
            startDate,
            endDate,
            { dryRun: false }
          );

          if (result.success && result.data) {
            console.log(
              `[PricingContext] Auto-recalculation complete: ${result.data.pricesUpdated} prices updated`
            );
          } else {
            console.warn(
              '[PricingContext] Auto-recalculation completed with errors:',
              result.error?.message || 'Unknown error'
            );
          }
        } catch (error) {
          console.error(
            '[PricingContext] Failed to auto-recalculate prices:',
            error
          );
        }
      }
    },
    [autoPushEnabled]
  );

  /**
   * Update Pricing
   */
  const updatePricing = useCallback(
    async (data: UpdatePricingInput) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        console.log('Starting price update for:', data);

        // Save to price storage service
        const result = await state.priceStorageService.savePrice({
          propertyId: data.propertyId,
          roomTypeId: data.roomTypeId,
          date: data.date,
          price: data.price,
          currency: data.currency || 'USD',
          createdBy: data.createdBy,
          adjustmentReason: data.adjustmentReason || 'Manual price update',
        });

        if (result.success) {
          console.log('Price saved successfully via PriceStorageService');

          // Update local state
          const pricingData: PricingData = {
            roomId: data.roomTypeId,
            date: data.date,
            price: data.price,
            occupancy: 0,
            available: 0,
            booked: 0,
            minStay: 1,
            maxStay: 7,
            currency: data.currency,
            lastUpdated: new Date(),
          };

          dispatch({ type: 'UPDATE_PRICING', payload: pricingData });

          // Save to Supabase
          try {
            const { error: rateError } = await supabase
              .from('current_room_rates')
              .upsert(
                {
                  property_id: data.propertyId,
                  room_type_id: data.roomTypeId,
                  date: data.date,
                  rate: data.price,
                  currency: data.currency || 'USD',
                  last_updated: new Date().toISOString(),
                  updated_by: data.createdBy,
                },
                {
                  onConflict: 'property_id,room_type_id,date,rate_plan',
                }
              );

            if (rateError) {
              console.warn(
                'Could not save rate to Supabase:',
                rateError.message
              );
            }

            // Log price change
            await supabase.from('price_logs').insert({
              property_id: data.propertyId,
              room_type_id: data.roomTypeId,
              date: data.date,
              old_price: null,
              new_price: data.price,
              change_reason: data.adjustmentReason || 'Manual price update',
              created_by: data.createdBy,
            });
          } catch (error) {
            console.warn('Supabase operations failed:', error);
          }

          console.log('Price update completed successfully');
        } else {
          throw new Error(result.errors?.[0]?.error || 'Failed to save price');
        }
      } catch (error) {
        console.error('Failed to update pricing:', error);
        dispatch({
          type: 'SET_ERROR',
          payload:
            error instanceof Error ? error.message : 'Failed to update price',
        });

        // Create alert for error
        const errorAlert: Alert = {
          id: Date.now().toString(),
          type: 'error',
          title: 'Price Update Failed',
          message:
            error instanceof Error ? error.message : 'Failed to update price',
          time: new Date(),
          priority: 'high',
          actionable: false,
          propertyId: data.propertyId,
          read: false,
        };
        dispatch({ type: 'ADD_ALERT', payload: errorAlert });
        throw error;
      }
    },
    [state.priceStorageService]
  );

  /**
   * Bulk Update Pricing
   */
  const bulkUpdatePricing = useCallback((data: PricingData[]) => {
    dispatch({ type: 'BULK_UPDATE_PRICING', payload: data });
  }, []);

  /**
   * Sync Pricing Data
   */
  const syncPricingData = useCallback(
    async (propertyId: string) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        // Get prices from Supabase
        const { data: supabasePrices, error } = await supabase
          .from('current_room_rates')
          .select('*')
          .eq('property_id', propertyId);

        if (error) {
          console.warn(
            'Could not sync pricing data from Supabase:',
            error.message
          );

          // Fallback to local storage data
          const prices = await state.priceStorageService.getPrices({
            propertyId,
          });

          const pricingData = prices.map((price) => ({
            roomId: price.roomTypeId,
            date: price.date,
            price: price.price,
            occupancy: 0,
            available: 0,
            booked: 0,
            minStay: 1,
            maxStay: 7,
            currency: price.currency,
            lastUpdated: price.updatedAt || price.createdAt,
          }));

          dispatch({ type: 'SYNC_PRICING_DATA', payload: pricingData });
          return;
        }

        if (supabasePrices && supabasePrices.length > 0) {
          const pricingData = supabasePrices.map((price) => ({
            roomId: price.room_type_id,
            date: price.date,
            price: price.rate,
            occupancy: price.occupancy || 0,
            available: price.available_rooms || 0,
            booked: price.booked_rooms || 0,
            minStay: price.min_stay || 1,
            maxStay: price.max_stay || 7,
            currency: price.currency,
            lastUpdated: new Date(price.last_updated),
          }));

          dispatch({ type: 'SYNC_PRICING_DATA', payload: pricingData });
        } else {
          dispatch({ type: 'SYNC_PRICING_DATA', payload: [] });
        }
      } catch (error) {
        console.error('Failed to sync pricing data:', error);
        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to sync pricing data',
        });
      }
    },
    [state.priceStorageService]
  );

  /**
   * Get Pricing Data
   */
  const getPricingData = useCallback(
    (roomId: string, date: string): PricingData | undefined => {
      return state.pricingData.find(
        (data) => data.roomId === roomId && data.date === date
      );
    },
    [state.pricingData]
  );

  /**
   * Load Rules
   */
  const loadRules = useCallback(async (propertyId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const { data, error } = await supabase
        .from('pricing_rules')
        .select('*')
        .eq('property_id', propertyId)
        .order('priority', { ascending: false });

      if (error) throw error;

      const rules: Rule[] = (data || []).map(
        (item: Record<string, unknown>) => {
          const parsedConditions = safeParseJson(item.conditions, []);
          const isMinLosRule = item.rule_type === 'min_los';

          let conditions = parsedConditions;
          let minLosConfig: MinLosConfig | undefined;

          if (
            isMinLosRule &&
            parsedConditions &&
            typeof parsedConditions === 'object' &&
            !Array.isArray(parsedConditions)
          ) {
            minLosConfig = (parsedConditions as { minLosConfig?: MinLosConfig })
              .minLosConfig;
            conditions =
              (parsedConditions as { conditions?: unknown[] }).conditions || [];
          }

          return {
            id: item.id,
            name: item.name || item.rule_name || '',
            description: '',
            conditions,
            actions: safeParseJson(item.actions, []),
            priority: item.priority,
            active: item.active,
            created: new Date(item.created_at),
            lastModified: new Date(item.updated_at),
            propertyId: item.property_id,
            scope: item.scope || 'global',
            roomTypeIds: item.room_type_ids || undefined,
            ruleType: (item.rule_type as RuleType) || 'pricing',
            minLosConfig,
          };
        }
      );

      dispatch({ type: 'SET_RULES', payload: rules });
    } catch (error) {
      console.error('Error loading rules:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load pricing rules' });
    }
  }, []);

  /**
   * Snapshot Rule to History
   * Helper function to save a rule snapshot before modifications
   */
  const snapshotRule = useCallback(
    async (
      rule: Rule,
      action: RuleHistoryAction,
      changeReason?: string
    ): Promise<void> => {
      try {
        // Get the next version number for this rule
        const { data: existingVersions, error: versionError } = await supabase
          .from('pricing_rules_history')
          .select('version')
          .eq('rule_id', rule.id)
          .order('version', { ascending: false })
          .limit(1);

        if (versionError) {
          console.warn(
            'Could not fetch version history:',
            versionError.message
          );
        }

        const nextVersion =
          existingVersions && existingVersions.length > 0
            ? existingVersions[0].version + 1
            : 1;

        // Insert the history entry
        // Note: pricing_rules_history uses JSONB for room_type_ids, so we need to stringify
        const { error } = await supabase.from('pricing_rules_history').insert({
          rule_id: rule.id,
          version: nextVersion,
          property_id: rule.propertyId,
          rule_name: rule.name,
          description: rule.description,
          rule_type: rule.ruleType || 'seasonal',
          conditions: rule.conditions,
          actions: rule.actions,
          active: rule.active,
          priority: rule.priority,
          scope: rule.scope,
          room_type_ids: rule.roomTypeIds || null,
          action: action,
          change_reason: changeReason,
          changed_at: new Date().toISOString(),
        });

        if (error) {
          console.warn('Could not save rule history:', error.message);
        }
      } catch (error) {
        console.warn('Failed to snapshot rule:', error);
      }
    },
    []
  );

  /**
   * Create Rule
   */
  const createRule = useCallback(
    async (input: CreateRuleInput, changeReason?: string): Promise<Rule> => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const newRule: Rule = {
          ...input,
          id: Date.now().toString(),
          created: new Date(),
          lastModified: new Date(),
          scope: input.scope || 'global',
          roomTypeIds: input.roomTypeIds,
          ruleType: input.ruleType || 'pricing',
          minLosConfig: input.minLosConfig,
        };

        const dbRuleType =
          input.ruleType === 'min_los' ? 'min_los' : 'seasonal';

        const conditionsWithMinLos =
          input.ruleType === 'min_los' && input.minLosConfig
            ? {
                minLosConfig: input.minLosConfig,
                conditions: newRule.conditions,
              }
            : newRule.conditions;

        const { data, error } = await supabase
          .from('pricing_rules')
          .insert({
            property_id: newRule.propertyId,
            name: newRule.name,
            rule_type: dbRuleType,
            conditions: JSON.stringify(conditionsWithMinLos),
            actions: JSON.stringify(newRule.actions),
            active: newRule.active,
            priority: newRule.priority,
            scope: newRule.scope,
            room_type_ids: newRule.roomTypeIds || null,
          })
          .select()
          .single();

        if (error) throw error;

        // Update the rule with the DB-generated ID
        newRule.id = data?.id || newRule.id;

        // Snapshot the newly created rule
        await snapshotRule(newRule, 'created', changeReason);

        dispatch({ type: 'ADD_RULE', payload: newRule });

        // Emit rule change event for workflow auto-trigger
        emitRuleChange('created', newRule.id, newRule.propertyId, newRule);

        return newRule;
      } catch (error) {
        console.error('Error creating rule:', error);
        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to create pricing rule',
        });
        throw error;
      }
    },
    [snapshotRule, emitRuleChange]
  );

  /**
   * Update Rule
   */
  const updateRule = useCallback(
    async (id: string, updates: UpdateRuleInput, changeReason?: string) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        // Fetch current rule state before update for history
        const currentRule = state.rules.find((r) => r.id === id);
        if (currentRule) {
          await snapshotRule(currentRule, 'updated', changeReason);
        }

        const updateData: Record<string, unknown> = {
          updated_at: new Date().toISOString(),
        };

        if (updates.name !== undefined) updateData['name'] = updates.name;
        if (updates.description !== undefined)
          updateData['description'] = updates.description;
        if (updates.conditions !== undefined)
          updateData['conditions'] = JSON.stringify(updates.conditions);
        if (updates.actions !== undefined)
          updateData['actions'] = JSON.stringify(updates.actions);
        if (updates.active !== undefined) updateData['active'] = updates.active;
        if (updates.priority !== undefined)
          updateData['priority'] = updates.priority;
        if (updates.scope !== undefined) updateData['scope'] = updates.scope;
        if (updates.roomTypeIds !== undefined)
          updateData['room_type_ids'] = updates.roomTypeIds;

        const { error } = await supabase
          .from('pricing_rules')
          .update(updateData)
          .eq('id', id);

        if (error) throw error;

        dispatch({ type: 'UPDATE_RULE', payload: { id, updates } });

        // Emit rule change event for workflow auto-trigger
        if (currentRule) {
          const updatedRule = { ...currentRule, ...updates };
          emitRuleChange('updated', id, currentRule.propertyId, updatedRule);
        }
      } catch (error) {
        console.error('Error updating rule:', error);
        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to update pricing rule',
        });
        throw error;
      }
    },
    [snapshotRule, state.rules, emitRuleChange]
  );

  /**
   * Delete Rule
   */
  const deleteRule = useCallback(
    async (id: string, changeReason?: string) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        // Snapshot the rule before deletion
        const currentRule = state.rules.find((r) => r.id === id);
        if (currentRule) {
          await snapshotRule(currentRule, 'deleted', changeReason);
        }

        const { error } = await supabase
          .from('pricing_rules')
          .delete()
          .eq('id', id);

        if (error) throw error;

        dispatch({ type: 'DELETE_RULE', payload: id });

        // Emit rule change event for workflow auto-trigger
        if (currentRule) {
          emitRuleChange('deleted', id, currentRule.propertyId);
        }
      } catch (error) {
        console.error('Error deleting rule:', error);
        dispatch({
          type: 'SET_ERROR',
          payload: 'Failed to delete pricing rule',
        });
        throw error;
      }
    },
    [snapshotRule, state.rules, emitRuleChange]
  );

  /**
   * Toggle Rule Active
   */
  const toggleRuleActive = useCallback(
    async (id: string, active: boolean) => {
      await updateRule(id, { active });
    },
    [updateRule]
  );

  /**
   * Get Rule History
   * Returns all history entries for a specific rule
   */
  const getRuleHistory = useCallback(
    async (ruleId: string): Promise<RuleHistoryEntry[]> => {
      try {
        const { data, error } = await supabase
          .from('pricing_rules_history')
          .select('*')
          .eq('rule_id', ruleId)
          .order('version', { ascending: false });

        if (error) throw error;

        return (data || []).map((item: Record<string, unknown>) => ({
          id: item.id,
          ruleId: item.rule_id,
          version: item.version,
          propertyId: item.property_id,
          rule: {
            id: item.rule_id,
            name: item.rule_name,
            description: item.description || '',
            conditions: safeParseJson(item.conditions, []),
            actions: safeParseJson(item.actions, []),
            priority: item.priority,
            active: item.active,
            created: new Date(item.changed_at),
            lastModified: new Date(item.changed_at),
            propertyId: item.property_id,
            scope: item.scope || 'global',
            roomTypeIds: item.room_type_ids || undefined,
          },
          action: item.action as RuleHistoryAction,
          changedBy: item.changed_by,
          changedAt: new Date(item.changed_at),
          changeReason: item.change_reason,
        }));
      } catch (error) {
        console.error('Error fetching rule history:', error);
        return [];
      }
    },
    []
  );

  /**
   * Revert to Version
   * Restores a rule to a specific historical version
   */
  const revertToVersion = useCallback(
    async (ruleId: string, version: number): Promise<Rule> => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        // Get the historical version
        const { data: historyData, error: historyError } = await supabase
          .from('pricing_rules_history')
          .select('*')
          .eq('rule_id', ruleId)
          .eq('version', version)
          .single();

        if (historyError || !historyData) {
          throw new Error('Version not found');
        }

        // Snapshot current state before reverting (if rule still exists)
        const currentRule = state.rules.find((r) => r.id === ruleId);
        if (currentRule) {
          await snapshotRule(
            currentRule,
            'updated',
            `Reverted to version ${version}`
          );
        }

        // Map frontend rule type to valid database type
        const validRuleTypes = [
          'high_occupancy',
          'low_occupancy',
          'booking_window',
          'seasonal',
          'event_based',
          'min_los',
          'gap_filling',
          'channel_override',
        ];
        const dbRuleType =
          historyData.rule_type &&
          validRuleTypes.includes(historyData.rule_type)
            ? historyData.rule_type
            : 'seasonal';

        // Update the rule with historical data
        const updateData: Record<string, unknown> = {
          rule_name: historyData.rule_name,
          description: historyData.description,
          rule_type: dbRuleType,
          conditions: historyData.conditions,
          actions: historyData.actions,
          active: historyData.active,
          priority: historyData.priority,
          scope: historyData.scope,
          room_type_ids: historyData.room_type_ids,
          updated_at: new Date().toISOString(),
        };

        // Restore the lead-time range only when the snapshot actually carries
        // one. Snapshots taken by this file's snapshotRule() work off the
        // in-memory Rule shape, which has never held these columns (same gap
        // valid_from/valid_until have), so they store NULL. Writing that NULL
        // back would widen the rule from "fires 4-8 days out" to "fires on
        // every date in its period" — a silent, repricing change. Keeping the
        // current range is the conservative direction.
        if (historyData.lead_time_min != null) {
          updateData['lead_time_min'] = historyData.lead_time_min;
        }
        if (historyData.lead_time_max != null) {
          updateData['lead_time_max'] = historyData.lead_time_max;
        }

        const { error: updateError } = await supabase
          .from('pricing_rules')
          .update(updateData)
          .eq('id', ruleId);

        if (updateError) throw updateError;

        // Build the restored rule object
        const restoredRule: Rule = {
          id: ruleId,
          name: historyData.rule_name,
          description: historyData.description || '',
          conditions: safeParseJson(historyData.conditions, []),
          actions: safeParseJson(historyData.actions, []),
          priority: historyData.priority,
          active: historyData.active,
          created: currentRule?.created || new Date(),
          lastModified: new Date(),
          propertyId: historyData.property_id,
          scope: historyData.scope || 'global',
          roomTypeIds: historyData.room_type_ids || undefined,
          ruleType: historyData.rule_type || 'pricing',
        };

        dispatch({
          type: 'UPDATE_RULE',
          payload: { id: ruleId, updates: restoredRule },
        });

        return restoredRule;
      } catch (error) {
        console.error('Error reverting to version:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to revert rule' });
        throw error;
      }
    },
    [snapshotRule, state.rules]
  );

  /**
   * Compare Versions
   * Returns a comparison between two versions of a rule
   */
  const compareVersions = useCallback(
    async (
      ruleId: string,
      v1: number,
      v2: number
    ): Promise<RuleVersionComparison> => {
      try {
        const { data, error } = await supabase
          .from('pricing_rules_history')
          .select('*')
          .eq('rule_id', ruleId)
          .in('version', [v1, v2]);

        if (error) throw error;

        const version1 = data?.find(
          (d: Record<string, unknown>) => d.version === v1
        );
        const version2 = data?.find(
          (d: Record<string, unknown>) => d.version === v2
        );

        if (!version1 || !version2) {
          throw new Error('One or both versions not found');
        }

        const parseRule = (item: Record<string, unknown>): Rule => ({
          id: item.rule_id,
          name: item.rule_name,
          description: item.description || '',
          conditions: safeParseJson(item.conditions, []),
          actions: safeParseJson(item.actions, []),
          priority: item.priority,
          active: item.active,
          created: new Date(item.changed_at),
          lastModified: new Date(item.changed_at),
          propertyId: item.property_id,
          scope: item.scope || 'global',
          roomTypeIds: item.room_type_ids || undefined,
        });

        const oldRule = parseRule(version1);
        const newRule = parseRule(version2);

        // Calculate differences
        const differences: RuleFieldDiff[] = [];

        if (oldRule.name !== newRule.name) {
          differences.push({
            field: 'name',
            label: 'Name',
            oldValue: oldRule.name,
            newValue: newRule.name,
            type: 'changed',
          });
        }

        if (oldRule.description !== newRule.description) {
          differences.push({
            field: 'description',
            label: 'Description',
            oldValue: oldRule.description,
            newValue: newRule.description,
            type: 'changed',
          });
        }

        if (oldRule.priority !== newRule.priority) {
          differences.push({
            field: 'priority',
            label: 'Priority',
            oldValue: oldRule.priority,
            newValue: newRule.priority,
            type: 'changed',
          });
        }

        if (oldRule.active !== newRule.active) {
          differences.push({
            field: 'active',
            label: 'Active',
            oldValue: oldRule.active,
            newValue: newRule.active,
            type: 'changed',
          });
        }

        if (oldRule.scope !== newRule.scope) {
          differences.push({
            field: 'scope',
            label: 'Scope',
            oldValue: oldRule.scope,
            newValue: newRule.scope,
            type: 'changed',
          });
        }

        if (
          JSON.stringify(oldRule.conditions) !==
          JSON.stringify(newRule.conditions)
        ) {
          differences.push({
            field: 'conditions',
            label: 'Conditions',
            oldValue: oldRule.conditions,
            newValue: newRule.conditions,
            type: 'changed',
          });
        }

        if (
          JSON.stringify(oldRule.actions) !== JSON.stringify(newRule.actions)
        ) {
          differences.push({
            field: 'actions',
            label: 'Actions',
            oldValue: oldRule.actions,
            newValue: newRule.actions,
            type: 'changed',
          });
        }

        if (
          JSON.stringify(oldRule.roomTypeIds) !==
          JSON.stringify(newRule.roomTypeIds)
        ) {
          differences.push({
            field: 'roomTypeIds',
            label: 'Room Types',
            oldValue: oldRule.roomTypeIds,
            newValue: newRule.roomTypeIds,
            type: 'changed',
          });
        }

        return {
          ruleId,
          oldVersion: v1,
          newVersion: v2,
          oldRule,
          newRule,
          differences,
        };
      } catch (error) {
        console.error('Error comparing versions:', error);
        throw error;
      }
    },
    []
  );

  /**
   * Get Deleted Rules
   * Returns all deleted rules for a property (within retention period)
   */
  const getDeletedRules = useCallback(
    async (propertyId: string): Promise<DeletedRuleInfo[]> => {
      try {
        // Get all history entries with 'deleted' action
        const { data, error } = await supabase
          .from('pricing_rules_history')
          .select('*')
          .eq('property_id', propertyId)
          .eq('action', 'deleted')
          .order('changed_at', { ascending: false });

        if (error) throw error;

        // Filter out rules that have been restored (exist in pricing_rules)
        const { data: activeRules } = await supabase
          .from('pricing_rules')
          .select('id')
          .eq('property_id', propertyId);

        const activeRuleIds = new Set(
          (activeRules || []).map((r: { id: string }) => r.id)
        );

        return (data || [])
          .filter(
            (item: Record<string, unknown>) =>
              !activeRuleIds.has(item.rule_id as string)
          )
          .map((item: Record<string, unknown>) => ({
            ruleId: item.rule_id,
            ruleName: item.rule_name,
            propertyId: item.property_id,
            deletedAt: new Date(item.changed_at),
            deletedBy: item.changed_by,
            changeReason: item.change_reason,
            lastVersion: item.version,
            rule: {
              id: item.rule_id,
              name: item.rule_name,
              description: item.description || '',
              conditions: safeParseJson(item.conditions, []),
              actions: safeParseJson(item.actions, []),
              priority: item.priority,
              active: item.active,
              created: new Date(item.changed_at),
              lastModified: new Date(item.changed_at),
              propertyId: item.property_id,
              scope: item.scope || 'global',
              roomTypeIds: item.room_type_ids || undefined,
            },
          }));
      } catch (error) {
        console.error('Error fetching deleted rules:', error);
        return [];
      }
    },
    []
  );

  /**
   * Restore Deleted Rule
   * Recreates a deleted rule from history
   */
  const restoreDeletedRule = useCallback(
    async (ruleId: string, version?: number): Promise<Rule> => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        // Get the history entry to restore from
        let query = supabase
          .from('pricing_rules_history')
          .select('*')
          .eq('rule_id', ruleId);

        if (version !== undefined) {
          query = query.eq('version', version);
        } else {
          // Get the most recent version (highest version number)
          query = query.order('version', { ascending: false }).limit(1);
        }

        const { data, error } = await query.single();

        if (error || !data) {
          throw new Error('Rule history not found');
        }

        // Recreate the rule
        const restoredRule: Rule = {
          id: ruleId,
          name: data.rule_name,
          description: data.description || '',
          conditions: safeParseJson(data.conditions, []),
          actions: safeParseJson(data.actions, []),
          priority: data.priority,
          active: data.active,
          created: new Date(),
          lastModified: new Date(),
          propertyId: data.property_id,
          scope: data.scope || 'global',
          roomTypeIds: data.room_type_ids || undefined,
          ruleType: data.rule_type || 'pricing',
        };

        // Map frontend rule type to valid database type
        const dbRuleType =
          data.rule_type &&
          [
            'high_occupancy',
            'low_occupancy',
            'booking_window',
            'seasonal',
            'event_based',
            'min_los',
            'gap_filling',
            'channel_override',
          ].includes(data.rule_type)
            ? data.rule_type
            : 'seasonal';

        // Insert the restored rule
        const { error: insertError } = await supabase
          .from('pricing_rules')
          .insert({
            id: restoredRule.id,
            property_id: restoredRule.propertyId,
            rule_name: restoredRule.name,
            description: restoredRule.description,
            rule_type: dbRuleType,
            conditions: restoredRule.conditions,
            actions: restoredRule.actions,
            active: restoredRule.active,
            priority: restoredRule.priority,
            scope: restoredRule.scope,
            room_type_ids: restoredRule.roomTypeIds || null,
            lead_time_min: data.lead_time_min ?? null,
            lead_time_max: data.lead_time_max ?? null,
            created_at: restoredRule.created.toISOString(),
            updated_at: restoredRule.lastModified.toISOString(),
          });

        if (insertError) throw insertError;

        // Snapshot the restoration
        await snapshotRule(restoredRule, 'created', 'Restored from deleted');

        dispatch({ type: 'ADD_RULE', payload: restoredRule });
        return restoredRule;
      } catch (error) {
        console.error('Error restoring deleted rule:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to restore rule' });
        throw error;
      }
    },
    [snapshotRule]
  );

  /**
   * Load Alerts
   */
  const loadAlerts = useCallback(async (propertyId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const { data, error } = await supabase
        .from('alerts')
        .select('*')
        .eq('property_id', propertyId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;

      const alerts: Alert[] = (data || []).map(
        (item: Record<string, unknown>) => ({
          id: item.id,
          type: item.type,
          title: item.title,
          message: item.message,
          time: new Date(item.created_at),
          priority: item.priority,
          actionable: item.actionable,
          propertyId: item.property_id,
          read: item.read,
        })
      );

      dispatch({ type: 'SET_ALERTS', payload: alerts });
    } catch (error) {
      console.error('Error loading alerts:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load alerts' });
    }
  }, []);

  /**
   * Create Alert
   */
  const createAlert = useCallback((input: CreateAlertInput) => {
    const newAlert: Alert = {
      ...input,
      id: Date.now().toString(),
      time: new Date(),
      read: false,
    };
    dispatch({ type: 'ADD_ALERT', payload: newAlert });

    // Persist only property-scoped alerts. The alerts table requires
    // property_id (NOT NULL) and RLS gates inserts on user_has_property_access,
    // so global toasts can't be saved — they live in local state only.
    if (!newAlert.propertyId) {
      return;
    }

    supabase
      .from('alerts')
      .insert({
        id: newAlert.id,
        property_id: newAlert.propertyId,
        type: newAlert.type,
        title: newAlert.title,
        message: newAlert.message,
        priority: newAlert.priority,
        actionable: newAlert.actionable,
        read: newAlert.read,
        created_at: newAlert.time.toISOString(),
      })
      .then(({ error }) => {
        if (error) {
          console.warn('Could not save alert to Supabase:', error.message);
        }
      });
  }, []);

  /**
   * Mark Alert Read
   */
  const markAlertRead = useCallback((id: string) => {
    dispatch({ type: 'MARK_ALERT_READ', payload: id });

    // Update in Supabase (fire and forget)
    supabase
      .from('alerts')
      .update({ read: true })
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.warn('Could not update alert in Supabase:', error.message);
        }
      });
  }, []);

  /**
   * Delete Alert
   */
  const deleteAlert = useCallback((id: string) => {
    dispatch({ type: 'DELETE_ALERT', payload: id });

    // Delete from Supabase (fire and forget)
    supabase
      .from('alerts')
      .delete()
      .eq('id', id)
      .then(({ error }) => {
        if (error) {
          console.warn('Could not delete alert from Supabase:', error.message);
        }
      });
  }, []);

  /**
   * Get Unread Alerts
   */
  const getUnreadAlerts = useCallback((): Alert[] => {
    return state.alerts.filter((alert) => !alert.read);
  }, [state.alerts]);

  const value: PricingContextValue = {
    // State
    pricingData: state.pricingData,
    rules: state.rules,
    alerts: state.alerts,
    isLoading: state.isLoading,
    error: state.error,

    // Actions
    updatePricing,
    bulkUpdatePricing,
    syncPricingData,
    getPricingData,
    loadRules,
    createRule,
    updateRule,
    deleteRule,
    toggleRuleActive,
    getRuleHistory,
    revertToVersion,
    compareVersions,
    getDeletedRules,
    restoreDeletedRule,
    loadAlerts,
    createAlert,
    markAlertRead,
    deleteAlert,
    getUnreadAlerts,

    // Rule Change Callbacks (for workflow auto-trigger)
    onRuleChange,
    autoPushEnabled,
    setAutoPushEnabled,
  };

  return (
    <PricingContext.Provider value={value}>{children}</PricingContext.Provider>
  );
};

/**
 * usePricing Hook
 * Custom hook to access Pricing context
 */
export const usePricing = (): PricingContextValue => {
  const context = useContext(PricingContext);

  if (context === undefined) {
    throw new Error('usePricing must be used within a PricingProvider');
  }

  return context;
};

export default PricingContext;
